import { PlatformRole } from "@proto/gen/users/v1/service"
import {
  Activity,
  BarChart2,
  BookOpen,
  Bot,
  Briefcase,
  CalendarClock,
  CandlestickChart,
  FlaskConical,
  Gauge,
  Globe,
  HandCoins,
  Layers,
  LineChart,
  MapPin,
  Network,
  ShieldAlert,
  SlidersHorizontal,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react"
import { type ActionId, canPerform } from "../auth/actions"
import type { AdminSection, NavItem } from "../types/navigation"

/**
 * Admin Simulator nav + `/admin/simulator` route: on in local `vite`, or when the bundle is built
 * with `VITE_ENABLE_ADMIN_SIMULATOR=true` (staging CI sets this).
 */
export const adminSimulatorEnabled =
  Boolean(import.meta.env.DEV) || import.meta.env.VITE_ENABLE_ADMIN_SIMULATOR === "true"

export const OPTIMIZER_SIMULATOR_PATH = "/admin/optimizer-simulator"

const simulatorNavItem: NavItem = {
  label: "Device Simulators",
  path: "/admin/simulator",
  icon: Activity,
  adminSection: "simulator",
  section: "Tools",
}

const optimizerSimulatorNavItem: NavItem = {
  label: "Optimizer Simulator",
  path: OPTIMIZER_SIMULATOR_PATH,
  icon: FlaskConical,
  adminSection: "optimizer-simulator",
  section: "Tools",
}

// Order matters: items are grouped under their `section` heading in the
// sidebar, so same-section entries are kept consecutive (GRIDBOX design).
export const adminNavigationConfig: NavItem[] = [
  {
    label: "Overview",
    path: "/admin",
    icon: Globe,
    adminSection: "overview",
  },
  // Platform
  {
    label: "Customers",
    path: "/admin/customers",
    icon: Briefcase,
    adminSection: "customers",
    section: "Platform",
  },
  {
    label: "Users",
    path: "/admin/users",
    icon: Users,
    adminSection: "users",
    section: "Platform",
  },
  {
    label: "Domain Entities",
    path: "/admin/domain",
    icon: Layers,
    adminSection: "domain",
    section: "Platform",
  },
  {
    label: "Schema ERD",
    path: "/admin/schema-erd",
    icon: Network,
    adminSection: "schema-erd",
    section: "Platform",
  },
  // Operations
  {
    label: "Bidding",
    // A gavel reads as a hammer (and so as "building"); offering capacity into
    // a market is better signalled by money changing hands.
    path: "/admin/bidding",
    icon: HandCoins,
    adminSection: "bidding",
    section: "Operations",
  },
  {
    label: "Dispatcher",
    path: "/admin/dispatcher",
    icon: Zap,
    adminSection: "dispatcher",
    section: "Operations",
  },
  {
    // FE1 "Controller plan" per site; same access as the Dispatcher.
    label: "Optimizer",
    path: "/admin/optimizer",
    icon: SlidersHorizontal,
    adminSection: "dispatcher",
    section: "Operations",
  },
  {
    label: "Sites",
    path: "/admin/sites",
    icon: MapPin,
    adminSection: "fleet",
    section: "Operations",
  },
  {
    // Renamed from "Fleet": this is a per-device monitor (batteries, chargers,
    // vehicles…) with filtering — "Devices" reads more accurately/professionally.
    label: "Devices",
    path: "/admin/devices",
    icon: Gauge,
    adminSection: "fleet",
    section: "Operations",
  },
  // Reports
  {
    label: "Report",
    path: "/admin/reports",
    icon: LineChart,
    adminSection: "reports",
    section: "Reports",
  },
  {
    label: "Bidding Report",
    path: "/admin/reports/bidding",
    icon: CandlestickChart,
    adminSection: "reports",
    section: "Reports",
  },
  {
    // Market prices (energy + capacity) with the Bidding Report's filters.
    label: "Market Report",
    path: "/admin/reports/market",
    icon: TrendingUp,
    adminSection: "reports",
    section: "Reports",
  },
  {
    // Demand-response programs (DR / OpenADR events), same layout as Bidding.
    label: "Programs Report",
    path: "/admin/reports/programs",
    icon: CalendarClock,
    adminSection: "reports",
    section: "Reports",
  },
  {
    label: "AWS Cost Reports",
    path: "/admin/aws-cost-reports",
    icon: BarChart2,
    adminSection: "aws-cost-reports",
    section: "Reports",
  },
  {
    label: "Lacework Alerts",
    path: "/admin/lacework-triage",
    icon: ShieldAlert,
    adminSection: "lacework-triage",
    section: "Reports",
  },
  // Tools
  ...(adminSimulatorEnabled ? [simulatorNavItem] : []),
  optimizerSimulatorNavItem,
  {
    label: "Agent",
    path: "/admin/agent",
    icon: Bot,
    adminSection: "agent",
    section: "Tools",
  },
  {
    label: "Docs",
    path: "/admin/docs",
    icon: BookOpen,
    adminSection: "docs",
    section: "Tools",
  },
]

/** Whether the given platform role may access an admin section. */
export function canAccessAdminSection(
  role: PlatformRole | undefined,
  section: AdminSection
): boolean {
  if (role === undefined || role === PlatformRole.UNSPECIFIED) return false
  return canPerform(role, `admin.section.${section}` as ActionId)
}

/**
 * Path prefixes → admin section, longest-prefix-first. Covers nav routes plus
 * detail/sub routes that have no nav entry (e.g. /admin/customers/:id,
 * /admin/schema-erd, /admin/charging-requirements which redirects into domain).
 */
const ADMIN_PATH_SECTIONS: ReadonlyArray<{ prefix: string; section: AdminSection }> = (
  [
    { prefix: "/admin/customers", section: "customers" },
    { prefix: "/admin/bidding", section: "bidding" },
    { prefix: "/admin/dispatcher", section: "dispatcher" },
    { prefix: "/admin/optimizer", section: "dispatcher" },
    { prefix: "/admin/microgrid", section: "fleet" },
    { prefix: "/admin/domain", section: "domain" },
    { prefix: "/admin/charging-requirements", section: "domain" },
    { prefix: "/admin/schema-erd", section: "schema-erd" },
    { prefix: "/admin/users", section: "users" },
    { prefix: "/admin/agent", section: "agent" },
    { prefix: "/admin/aws-cost-reports", section: "aws-cost-reports" },
    { prefix: "/admin/lacework-triage", section: "lacework-triage" },
    { prefix: "/admin/e2e", section: "e2e" },
    { prefix: "/admin/reports", section: "reports" },
    { prefix: "/admin/docs", section: "docs" },
    { prefix: "/admin/simulator", section: "simulator" },
    { prefix: OPTIMIZER_SIMULATOR_PATH, section: "optimizer-simulator" },
    { prefix: "/admin/devices", section: "fleet" },
    { prefix: "/admin/sites", section: "fleet" },
    // Legacy alias that redirects to /admin/devices; gated the same way.
    { prefix: "/admin/fleet", section: "fleet" },
  ] satisfies Array<{ prefix: string; section: AdminSection }>
).sort((a, b) => b.prefix.length - a.prefix.length)

/** Resolve the admin section for a pathname, or "overview" for the /admin index. */
export function resolveAdminSection(pathname: string): AdminSection | null {
  if (pathname === "/admin" || pathname === "/admin/") return "overview"
  const match = ADMIN_PATH_SECTIONS.find(
    (entry) => pathname === entry.prefix || pathname.startsWith(`${entry.prefix}/`)
  )
  return match?.section ?? null
}

/** Drop admin nav items the given platform role can't access. */
export function filterAdminNavForRole(items: NavItem[], role: PlatformRole | undefined): NavItem[] {
  return items.filter(
    (item) => !item.adminSection || canAccessAdminSection(role, item.adminSection)
  )
}
